import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Minimize2,
  Maximize2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** The page that holds the real answer, offered under the message. */
  link?: { to: string; label: string };
  timestamp: Date;
}

/**
 * What the assistant can say today: where each answer lives.
 *
 * There is no chat service behind this panel, so it cannot read the account.
 * It used to answer every reader with the same invented figures (a monthly
 * spend, a savings total, a resource count) that read as their own. It now
 * says so and points to the page that has the real figure. Picked by keyword
 * in `answerFor`.
 */
const ANSWERS = {
  cost: {
    content:
      "I can't read your spend yet. Cost Analysis has it, by provider, cloud credential and service, over any period.",
    link: { to: "/costs", label: "Open Cost Analysis" },
  },
  save: {
    content:
      "I can't work out savings yet. Recommendations lists what your providers suggest and what each is worth.",
    link: { to: "/recommendations", label: "Open Recommendations" },
  },
  alert: {
    content:
      "I can't read your alerts yet. The Alerts page lists them and the rules that raise them.",
    link: { to: "/alerts", label: "Open Alerts" },
  },
  inventory: {
    content:
      "I can't read your resources yet. Inventory lists every resource your cloud credentials reach.",
    link: { to: "/inventory", label: "Open Inventory" },
  },
  help: {
    content:
      "Answers from your own account are coming soon. Until then I can point you to the page for costs, savings, alerts or inventory.",
  },
  fallback: {
    content:
      "I can't answer that yet. Ask about costs, savings, alerts or inventory and I'll point you to the page that has it.",
  },
} as const satisfies Record<string, Pick<Message, "content" | "link">>;

function answerFor(message: string): Pick<Message, "content" | "link"> {
  const lowerMessage = message.toLowerCase();

  // Savings before cost: "save money" is a question about savings.
  if (
    lowerMessage.includes("save") ||
    lowerMessage.includes("saving") ||
    lowerMessage.includes("optimize") ||
    lowerMessage.includes("reduce")
  ) {
    return ANSWERS.save;
  }
  if (
    lowerMessage.includes("cost") ||
    lowerMessage.includes("spend") ||
    lowerMessage.includes("money")
  ) {
    return ANSWERS.cost;
  }
  if (lowerMessage.includes("alert") || lowerMessage.includes("notification")) {
    return ANSWERS.alert;
  }
  if (
    lowerMessage.includes("inventory") ||
    lowerMessage.includes("resource") ||
    lowerMessage.includes("instance")
  ) {
    return ANSWERS.inventory;
  }
  if (
    lowerMessage.includes("help") ||
    lowerMessage.includes("what can") ||
    lowerMessage.includes("how")
  ) {
    return ANSWERS.help;
  }

  return ANSWERS.fallback;
}

export function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Hi. Answers from your own account are coming soon. For now I can point you to the page that has what you're looking for.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /*
   * Answered at once. There is nothing to wait for, so a typing indicator and
   * a pause would only pretend to be working something out.
   */
  const send = (text: string) => {
    const question = text.trim();
    if (!question) return;
    const now = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: `${now}-q`,
        role: "user",
        content: question,
        timestamp: new Date(now),
      },
      {
        id: `${now}-a`,
        role: "assistant",
        ...answerFor(question),
        timestamp: new Date(now),
      },
    ]);
    setInput("");
  };

  const handleSend = () => send(input);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full"
        size="icon"
        aria-label="Open assistant"
      >
        <MessageCircle className="h-6 w-6" aria-hidden />
      </Button>
    );
  }

  return (
    <div
      className={cn(
        // Floats over the page, so it sits on `--popover` with a full-strength
        // border. The border carries the separation; the shadow only does work
        // in the light theme.
        "fixed bottom-6 right-6 z-50 flex flex-col rounded-lg border border-border bg-popover shadow-lg transition-all duration-300",
        // Never wider than the screen less its margins: at phone width a
        // fixed 384px panel ran past the left edge.
        "max-w-[calc(100vw-3rem)]",
        isMinimized ? "h-14 w-80" : "h-[min(500px,calc(100svh-3rem))] w-96",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-lg border-b border-border bg-muted/30 p-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <Bot className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">EverythingCloud AI</p>
            {!isMinimized && (
              <p className="text-xs text-muted-foreground">
                Account answers coming soon
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsMinimized(!isMinimized)}
            aria-label={isMinimized ? "Expand assistant" : "Minimize assistant"}
          >
            {isMinimized ? (
              <Maximize2 className="h-4 w-4" aria-hidden />
            ) : (
              <Minimize2 className="h-4 w-4" aria-hidden />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsOpen(false)}
            aria-label="Close assistant"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div
            role="log"
            aria-label="Conversation with the assistant"
            className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-2",
                  message.role === "user" ? "flex-row-reverse" : "flex-row",
                )}
              >
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback
                    className={cn(
                      "text-xs",
                      message.role === "assistant"
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {message.role === "assistant" ? (
                      <Bot className="h-4 w-4" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                  </AvatarFallback>
                </Avatar>
                <div
                  className={cn(
                    "max-w-[80%] rounded-md px-3 py-2 text-sm",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground",
                  )}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  {message.link && (
                    <Link
                      to={message.link.to}
                      className="mt-1.5 inline-block font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {message.link.label}
                    </Link>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-4 pb-2">
            <div className="flex flex-wrap gap-1">
              {["Show my costs", "Find savings", "View alerts"].map(
                (action) => (
                  // A button, not a clickable chip: it has to be reachable
                  // from the keyboard. Same badge look.
                  <button
                    key={action}
                    type="button"
                    className={cn(
                      badgeVariants({ variant: "secondary" }),
                      "cursor-pointer hover:bg-secondary/80 text-xs",
                    )}
                    onClick={() => send(action)}
                  >
                    {action}
                  </button>
                ),
              )}
            </div>
          </div>

          {/* Input */}
          <div className="border-t border-border p-3">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about costs, savings, alerts..."
                className="flex-1"
                aria-label="Message the assistant"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label="Send message"
              >
                <Send className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
